import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Clock, HelpCircle, Search, X } from 'lucide-react';
import BackButton from '../components/BackButton';
import BBALoader from '../components/BBALoader';
import PortalBackground from '../components/PortalBackground';
import CrmStatCard from '../components/crm/CrmStatCard';
import { FIELD_CLASS, TH_CLASS, FilterSelect } from '../components/crm/CrmFilterUi';
import { getAllQuestions, subscribeToQuestions } from '../services/firebase/questions';
import { useAuth } from '../contexts/AuthContext';

/** Status filter value: Open or In Progress (excludes Resolved). */
const STATUS_NOT_RESOLVED = '__not_resolved__';

// Status pill palette (dot + text + hairline border on a tinted surface)
const STATUS_PILL = {
  Open: 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-300 dark:border-yellow-800/60',
  'In Progress': 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-800/60',
  Resolved: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800/60',
};

const QuestionsPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const assigneeDefaultAppliedRef = useRef(false);
  
  // State
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [accountFilter, setAccountFilter] = useState('');
  const [assignedFilter, setAssignedFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(STATUS_NOT_RESOLVED);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Load questions on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const questionsData = await getAllQuestions();
        setQuestions(questionsData);
        setLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Set up real-time listener for questions
  useEffect(() => {
    const unsubscribe = subscribeToQuestions((questionsData) => {
      setQuestions(questionsData);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!currentUser || assigneeDefaultAppliedRef.current) return;
    assigneeDefaultAppliedRef.current = true;
    const me =
      (currentUser.displayName && currentUser.displayName.trim()) ||
      currentUser.email ||
      '';
    if (me) setAssignedFilter(me);
  }, [currentUser]);

  // Calculate quick stats
  const quickStats = {
    open: questions.filter(question => question.status === 'Open').length,
    inProgress: questions.filter(question => question.status === 'In Progress').length,
    resolved: questions.filter(question => question.status === 'Resolved').length
  };

  // Get unique values for filters
  const accounts = [...new Set(questions.map(question => question.companyName))];
  const meLabel =
    (currentUser?.displayName && currentUser.displayName.trim()) ||
    currentUser?.email ||
    '';
  const assignedToOptions = [
    ...new Set([
      ...(meLabel ? [meLabel] : []),
      ...questions.map(question => question.assigneeName).filter(Boolean),
    ]),
  ];
  const statusOptions = ["Open", "In Progress", "Resolved"];

  // Filter questions
  const filterQuestions = (questions) => {
    return questions.filter(question => {
      const searchMatch = !search || 
        (question.questionText?.toLowerCase() || '').includes(search.toLowerCase()) ||
        (question.companyName?.toLowerCase() || '').includes(search.toLowerCase()) ||
        (question.assigneeName?.toLowerCase() || '').includes(search.toLowerCase());
      
      const accountMatch = !accountFilter || question.companyName === accountFilter;
      const assignedMatch = !assignedFilter || question.assigneeName === assignedFilter;
      const statusMatch =
        !statusFilter ||
        (statusFilter === STATUS_NOT_RESOLVED
          ? question.status === 'Open' || question.status === 'In Progress'
          : question.status === statusFilter);
      const dateMatch = (!startDate || question.createdAt >= new Date(startDate)) && 
                        (!endDate || question.createdAt <= new Date(endDate));
      
      return searchMatch && accountMatch && assignedMatch && statusMatch && dateMatch;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen p-4 md:p-6 relative">
        <PortalBackground />
        <div className="relative flex items-center justify-center py-32">
          <BBALoader size={48} />
        </div>
      </div>
    );
  }

  const visibleQuestions = filterQuestions(questions);

  return (
    <div className="min-h-screen p-4 md:p-6 relative">
      {/* Same ambient BBA background as the Leads, Clients and Tasks pages */}
      <PortalBackground />
      <div className="relative container mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <BackButton onClick={() => navigate('/crm')} />
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">Questions</h1>
        <div className="w-10"></div> {/* Spacer to center the title */}
      </div>

      {/* Quick Stats Section */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6">
        <CrmStatCard icon={HelpCircle} label="Open" value={quickStats.open} tone="yellow" />
        <CrmStatCard icon={Clock} label="In Progress" value={quickStats.inProgress} tone="orange" />
        <CrmStatCard icon={CheckCircle2} label="Resolved" value={quickStats.resolved} tone="green" />
      </div>

      {/* Questions Section */}
      <div>
        {/* Filter Row */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[16rem]">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="search"
              placeholder="Search questions by text, account, or assignee…"
              className={`${FIELD_CLASS} pl-10 pr-10 [&::-webkit-search-cancel-button]:hidden`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-700"
                title="Clear search"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <FilterSelect
            value={accountFilter}
            onChange={(e) => setAccountFilter(e.target.value)}
            className="w-full sm:w-48"
            aria-label="Account"
          >
            <option value="">All Accounts</option>
            {accounts.map((account, idx) => (
              <option key={idx} value={account}>{account}</option>
            ))}
          </FilterSelect>
          <FilterSelect
            value={assignedFilter}
            onChange={(e) => setAssignedFilter(e.target.value)}
            className="w-full sm:w-48"
            aria-label="Assignee"
          >
            <option value="">All Assignees</option>
            {assignedToOptions.map((assignee, idx) => (
              <option key={idx} value={assignee}>{assignee}</option>
            ))}
          </FilterSelect>
          <FilterSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full sm:w-52"
            aria-label="Status"
          >
            <option value="">All Status</option>
            <option value={STATUS_NOT_RESOLVED}>Not resolved (Open & In progress)</option>
            {statusOptions.map((status, idx) => (
              <option key={idx} value={status}>{status}</option>
            ))}
          </FilterSelect>
          <div className="flex gap-2 w-full sm:w-auto">
            <input
              type="date"
              className={`${FIELD_CLASS} flex-1 sm:w-40 px-3`}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              aria-label="Start date"
              title="Created on or after"
            />
            <input
              type="date"
              className={`${FIELD_CLASS} flex-1 sm:w-40 px-3`}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              aria-label="End date"
              title="Created on or before"
            />
          </div>
        </div>

        {/* Questions Table */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
          <div className="relative flex items-center justify-center gap-2.5 px-5 py-4 border-b border-gray-200 dark:border-gray-800">
            <span className="text-base font-semibold text-gray-900 dark:text-white">
              {statusFilter === STATUS_NOT_RESOLVED ? 'Unresolved Questions' : statusFilter ? `${statusFilter} Questions` : 'All Questions'}
            </span>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
              {visibleQuestions.length}
            </span>
          </div>
          <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-800">
              <tr>
                <th className={TH_CLASS}>Account</th>
                <th className={TH_CLASS}>Question</th>
                <th className={TH_CLASS}>Date Created</th>
                <th className={TH_CLASS}>Assigned To</th>
                <th className={TH_CLASS}>Status</th>
              </tr>
            </thead>
            <tbody>
              {visibleQuestions.map(question => (
                <tr
                  key={question.id}
                  onClick={() => navigate(`/crm/questions/${question.id}`)}
                  className="border-b border-gray-100 dark:border-gray-800 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-800/60 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3.5 text-sm text-gray-600 dark:text-gray-300">{question.companyName}</td>
                  <td className="px-4 py-3.5 text-sm font-medium text-gray-900 dark:text-gray-100">
                    {question.questionText?.length > 100
                      ? `${question.questionText.substring(0, 100)}...`
                      : question.questionText}
                  </td>
                  <td className="px-4 py-3.5 text-sm text-gray-600 dark:text-gray-300">
                    {question.createdAt ? new Date(question.createdAt).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }) : 'N/A'}
                  </td>
                  <td className="px-4 py-3.5 text-sm text-gray-600 dark:text-gray-300">
                    {question.assigneeName || 'Unassigned'}
                  </td>
                  <td className="px-4 py-3.5 text-sm">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${STATUS_PILL[question.status] || STATUS_PILL.Resolved}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-current" />
                      {question.status}
                    </span>
                  </td>
                </tr>
              ))}
              {visibleQuestions.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-4 py-12 text-sm text-center text-gray-500 dark:text-gray-400">No questions match these filters</td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default QuestionsPage;
